import subDays from 'date-fns/subDays';
import { saveAs } from 'file-saver';
import { formatDate } from 'helpers/date';
import * as Rx from 'rxjs';
import { debounceTime, finalize, map, switchMap, take, tap } from 'rxjs/operators';
import type { IAffiliate, IFilter, IFilterAPI, IFilterMerged } from 'v2/interfaces/affiliate';
import type { IPaginationParams, IPaginationResponse } from 'v2/interfaces/pagination';
import type { ITableSort } from 'v2/interfaces/table';

import apiService from '../api';
import type { ApiService } from '../api';

export const getInitialFilter = (): IFilter => ({
  date: [formatDate(subDays(new Date(), 7), 'dd/MM/yyyy'), formatDate(new Date(), 'dd/MM/yyyy')],
  status: 'pending',
  affiliate_type: 'affiliate_name',
  affiliate_value: '',
  product_id: []
});

export const InitialFilter: IFilter = getInitialFilter();

const InitialPagination: IPaginationParams = {
  current_page: 1,
  per_page: 10
};

class AffiliateService {
  private loadingExporting$ = new Rx.BehaviorSubject<boolean>(false);

  private filters$ = new Rx.BehaviorSubject<IFilter>(InitialFilter);

  private loadingAffiliates$ = new Rx.BehaviorSubject<boolean>(true);
  private affiliates$ = new Rx.BehaviorSubject<Array<IAffiliate>>([]);

  private paginationParams$ = new Rx.BehaviorSubject<IPaginationParams>(InitialPagination);
  private paginationResponse$ = new Rx.BehaviorSubject<IPaginationResponse>(null);

  private ordering$ = new Rx.BehaviorSubject<boolean | 'asc' | 'desc'>(false);
  private ordered$ = new Rx.BehaviorSubject<number>(-1);
  private currentSort$ = new Rx.BehaviorSubject<ITableSort>(null);

  private totalItems$ = new Rx.BehaviorSubject<number>(0);
  //private showSales$ = new Rx.BehaviorSubject<boolean>(false);

  constructor(private apiService: ApiService) {}

  public loadingExporting = () => this.loadingExporting$;

  public setFilter(filter: IFilter): void {
    this.loadingAffiliates$.next(true);

    this.filters$.pipe(take(1)).subscribe((currentFilter: IFilter) => {
      if (filter.status && filter.status !== currentFilter.status) {
        filter.order = null;

        this.setOrdering(false);
        this.setOrdered(-1);
        this.setCurrentSort(null);
      }

      this.filters$.next({ ...currentFilter, ...filter });
    });
  }

  public getTotalItems = (): Rx.Observable<number> => this.totalItems$;

  public getOrdering = (): Rx.Observable<boolean | 'asc' | 'desc'> => this.ordering$;

  public setOrdering(ordering: boolean | 'asc' | 'desc'): void {
    this.ordering$.next(ordering);
  }

  public getOrdered = (): Rx.Observable<number> => this.ordered$;

  public setOrdered(ordered: number): void {
    this.ordered$.next(ordered);
  }

  public getCurrentSort = (): Rx.Observable<ITableSort> => this.currentSort$;

  public setCurrentSort(sort: ITableSort): void {
    this.currentSort$.next(sort);
  }

  public getFilters = (): Rx.Observable<IFilter> => this.filters$;

  public getPaginationParams = (): Rx.Observable<IPaginationParams> => this.paginationParams$;

  public getPaginationResponse = (): Rx.Observable<IPaginationResponse> => this.paginationResponse$;

  public setPagination(pagination: IPaginationParams): void {
    this.paginationParams$.next(pagination);
  }

  public resetPage = () => {
    this.paginationParams$.next({ ...this.paginationParams$.value, current_page: 1 });
    return this.getPaginationParams();
  };

  public isLoading = () => this.loadingAffiliates$;

  public getAffiliates = (): Rx.Observable<Array<IAffiliate>> => this.affiliates$;

  public moderateAffiliate(id: number, status: 'A' | 'R') {
    this.loadingAffiliates$.next(true);

    return this.apiService.post(`/affiliates/${id}/moderate`, { status }).pipe(tap(() => this.updateListAffiliates()));
  }

  public moderateMultipleAffiliates(affiliates: Array<number>, status: 'A' | 'R') {
    this.loadingAffiliates$.next(true);

    return this.apiService
      .put('/affiliates/moderate', { affiliates, status })
      .pipe(tap(() => this.updateListAffiliates()));
  }

  public prepareDataForApi = (filters: IFilterMerged): IFilterAPI => {
    const filterInputForApi: IFilterAPI = {
      status: filters.status,
      product_id: filters.product_id,
      orderby: filters.order,
      coproducer: true,
      request_date_initial: filters.date[0],
      request_date_final: filters.date[1]
    };

    if (filters.current_page) {
      filterInputForApi.current_page = filters.current_page;
    }

    if (filters.per_page) {
      filterInputForApi.per_page = filters.per_page;
    }

    if (filters.export) {
      filterInputForApi.export = filters.export;
      filterInputForApi.responseType = filters.responseType || 'blob';
    }

    if (filters.affiliate_type === 'affiliate_name' && String(filters.affiliate_value).length > 0) {
      filterInputForApi.affiliate_name = filters.affiliate_value;
    }

    if (filters.affiliate_type === 'affiliate_email' && String(filters.affiliate_value).length > 0) {
      filterInputForApi.affiliate_email = filters.affiliate_value;
    }

    return filterInputForApi;
  };

  public updateListAffiliates = () => {
    this.loadingAffiliates$.next(true);

    this.getFilters()
      .pipe(
        switchMap(filters => this.getPaginationParams().pipe(map(params => ({ ...filters, ...params })))),
        switchMap(filters => this.apiService.get('affiliates', this.prepareDataForApi(filters))),
        tap(response => this.affiliates$.next(response.data)),
        //tap(response => this.showSales$.next(!!!response.pagination.sales_info)),
        tap(response => this.paginationResponse$.next(response.pagination)),
        tap(() => this.loadingAffiliates$.next(false)),
        take(1)
      )
      .subscribe();
  };

  public loadAffiliates() {
    return this.getFilters().pipe(
      switchMap(filters => this.resetPage().pipe(map(params => ({ ...filters, ...params })))),
      debounceTime(10),
      switchMap(filters => {
        this.loadingAffiliates$.next(true);
        return this.apiService
          .get('affiliates', this.prepareDataForApi(filters))
          .pipe(finalize(() => this.loadingAffiliates$.next(false)));
      }),
      tap(response => this.affiliates$.next(response.data)),
      //tap(response => this.showSales$.next(!!!response.pagination.sales_info)),
      tap(response => this.paginationResponse$.next(response.pagination)),
      tap(response => this.totalItems$.next(response?.pagination?.total_items))
    );
  }

  public sendMessageBroadcast = (message: string, affiliates: Array<number>, sendEmail: boolean) => {
    return this.apiService.post('/chat/broadcast', { content: message, customers: affiliates, send_email: sendEmail });
  };

  public configCustomCommission = (affiliate: number, type?: 'P' | 'V', value?: number) => {
    if (!type && !value) {
      return this.apiService
        .post(`/affiliates/${affiliate}/commission`, { type: null, value: 0 })
        .pipe(tap(() => this.updateListAffiliates()));
    }

    return this.apiService
      .post(`/affiliates/${affiliate}/commission`, { type, value })
      .pipe(tap(() => this.updateListAffiliates()));
  };

  public export = () => {
    this.loadingExporting$.next(true);

    const prepareDataForApi = this.prepareDataForApi;

    return this.getFilters().pipe(
      switchMap(filters => {
        const filterDataForApi = prepareDataForApi({ ...filters, export: true, responseType: 'blob' });

        return this.apiService.get('/affiliates', filterDataForApi);
      }),
      tap(response => {
        const date = formatDate(new Date(), 'dd-MM-yyyy-hh-mm-ss');
        const fileExport = new Blob([response], { type: 'text/csv; charset=utf-8' });
        saveAs(fileExport, `meus-afiliados-${date}.csv`);
      }),
      tap(() => this.loadingExporting$.next(false)),
      take(1)
    );
  };

  public inviteAffiliate(productId: number, recruiterId: number, affiliateId: number) {
    if (!recruiterId) {
      return this.apiService
        .post(`/showcase/${productId}/affiliate`, {
          affiliateId,
          message: 'Olá, gostaria de afiliar-se ao seu produto.'
        })
        .pipe(tap(response => response));
    }

    return this.apiService
      .post(`/showcase/${productId}/affiliate`, {
        recruiterId,
        affiliateId,
        message: 'Olá, gostaria de afiliar-se ao seu produto.'
      })
      .pipe(tap(response => response));
  }

  public cleanFilter() {
    this.filters$.next(getInitialFilter());
  }

  // public getShowSales = () => this.showSales$;

  // public setShowSales = (show: boolean) => {
  //   return Rx.of(true).pipe(
  //     tap(() => {
  //       this.showSales$.next(show);
  //     })
  //   );
  // };

  public addAffiliateInvoice(invoiceId: number, affiliateEmail: string) {
    return this.apiService
      .put(`/invoices/${invoiceId}/affiliate`, { affiliate_email: affiliateEmail }, {}, false, true)
      .toPromise()
      .then(res => res.data);
  }
}

const affiliateService = new AffiliateService(apiService);
export default affiliateService;
